import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../api";

/**
 * Self-serve web/API pen-test engagement for a scope: the customer supplies two test accounts,
 * the object endpoints to test, and safety restrictions (read-only / allowed methods), then triggers
 * the run. The target hosts are the scope's DOMAIN members (RoE), enforced server-side.
 */
const METHODS = ["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE"];

export default function ScopePentestPanel({ scopeId }) {
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [specUrl, setSpecUrl] = useState("");
  const [readOnly, setReadOnly] = useState(true);
  const [allowed, setAllowed] = useState(["GET", "HEAD"]);
  const [idA, setIdA] = useState({ label: "", headerName: "Authorization", headerValue: "" });
  const [idB, setIdB] = useState({ label: "", headerName: "Authorization", headerValue: "" });
  const [objects, setObjects] = useState([]);
  const [privileged, setPrivileged] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get(`/iasm/scope/scopes/${scopeId}/pentest`);
      const c = r.data || {};
      setCfg(c);
      setBaseUrl(c.baseUrl || "");
      setSpecUrl(c.specUrl || "");
      setReadOnly(c.readOnly !== false);
      setAllowed((c.allowedMethods || "GET,HEAD").split(",").map(s => s.trim()).filter(Boolean));
      setIdA({ label: c.identityA?.label || "", headerName: c.identityA?.headerName || "Authorization", headerValue: "" });
      setIdB({ label: c.identityB?.label || "", headerName: c.identityB?.headerName || "Authorization", headerValue: "" });
      setObjects(c.objects || []);
      setPrivileged(c.privilegedEndpoints || []);
    } catch (e) { /* new scope, no config yet */ setCfg({ configured: false }); }
  }, [scopeId]);

  useEffect(() => { setResult(null); setMsg(null); load(); }, [load]);

  // Persist the current form. Returns true on success. (Extracted so Run can save-first.)
  const persist = async () => {
    await axiosInstance.put(`/iasm/scope/scopes/${scopeId}/pentest`, {
      baseUrl, specUrl, readOnly, allowedMethods: allowed.join(","),
      identityA: idA, identityB: idB, objects, privilegedEndpoints: privileged,
    });
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try { await persist(); setMsg("Saved."); await load(); }
    catch (e) { setMsg("Save failed: " + (e.response?.data?.message || e.message)); }
    setBusy(false);
  };

  // Cases the run will actually execute: object endpoints need BOTH A and B ids; privileged run as-is.
  const objectCases = objects.filter(o => o.path_template && (o.a_ids || []).length && (o.b_ids || []).length).length;
  const runnableCases = objectCases + privileged.filter(p => p.path).length;

  const run = async () => {
    setMsg(null); setResult(null);
    const spec = specUrl.trim();
    if (!spec && !baseUrl.trim()) { setMsg("Set the application base URL (or a Swagger/OpenAPI URL) first."); return; }
    if (!idA.headerValue && !cfg?.identityA?.configured) { setMsg("Add test identity A (auth header value)."); return; }
    if (!idB.headerValue && !cfg?.identityB?.configured) { setMsg("Add test identity B (auth header value)."); return; }
    if (!spec && runnableCases === 0) {
      setMsg("Nothing to test yet — add a Swagger/OpenAPI URL, or an object endpoint with BOTH A ids and B ids.");
      return;
    }
    setBusy(true);
    try {
      // Run always uses the SAVED config, so persist the current form first (no silent stale run).
      await persist();
      const r = await axiosInstance.post(`/iasm/scope/scopes/${scopeId}/pentest/run`);
      setResult(r.data);
      if (r.data.error) setMsg("Engine: " + r.data.error);
      else if (!r.data.casesRun) setMsg("The engine ran but tested 0 cases — check that each object endpoint has both A and B ids.");
      await load();
    } catch (e) { setMsg("Run failed: " + (e.response?.data?.message || e.message)); }
    setBusy(false);
  };

  const box = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16, marginBottom: 16 };
  const label = { fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 };
  const input = { padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 };
  const btn = { padding: "8px 16px", background: "#4f46e5", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 };

  const addObj = () => setObjects([...objects, { name: "", method: "GET", path_template: "", a_ids: [], b_ids: [] }]);
  const setObj = (i, k, v) => setObjects(objects.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const setObjIds = (i, k, v) => setObj(i, k, v.split(",").map(s => s.trim()).filter(Boolean));
  const delObj = (i) => setObjects(objects.filter((_, j) => j !== i));
  const addPriv = () => setPrivileged([...privileged, { name: "", method: "GET", path: "" }]);
  const setPriv = (i, k, v) => setPrivileged(privileged.map((o, j) => j === i ? { ...o, [k]: v } : o));
  const delPriv = (i) => setPrivileged(privileged.filter((_, j) => j !== i));
  const toggleMethod = (m) => setAllowed(allowed.includes(m) ? allowed.filter(x => x !== m) : [...allowed, m]);

  return (
    <div style={box}>
      <div style={{ ...label, display: "flex", alignItems: "center", gap: 8 }}>
        <i className="fas fa-user-secret" style={{ color: "#4f46e5" }} /> Web / API Pen-Test Engagement
      </div>

      {/* target + identities */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Application base URL</div>
          <input style={{ ...input, width: "100%", boxSizing: "border-box" }} placeholder="https://koorierinc.net"
            value={baseUrl} onChange={e => setBaseUrl(e.target.value)} />
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            Authorized hosts (from scope): {(cfg?.allowlist || []).join(", ") || "add a DOMAIN to the scope"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
            Swagger / OpenAPI URL <span style={{ color: "#4f46e5" }}>· recommended</span>
          </div>
          <input style={{ ...input, width: "100%", boxSizing: "border-box" }} placeholder="https://koorierinc.net/v3/api-docs (or swagger.json)"
            value={specUrl} onChange={e => setSpecUrl(e.target.value)} />
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            When set, the engine auto-discovers every object endpoint and harvests each identity's own ids — object endpoints below become optional.
          </div>
        </div>
        {[["A", idA, setIdA], ["B", idB, setIdB]].map(([k, id, setId]) => (
          <div key={k}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
              Test identity {k} {id === idA ? "" : ""} {cfg?.[`identity${k}`]?.configured ? "· saved ✓" : ""}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input style={{ ...input, width: 110 }} placeholder={`label (user-${k.toLowerCase()})`} value={id.label} onChange={e => setId({ ...id, label: e.target.value })} />
              <input style={{ ...input, width: 120 }} placeholder="header" value={id.headerName} onChange={e => setId({ ...id, headerName: e.target.value })} />
              <input style={{ ...input, flex: 1 }} type="password" placeholder={cfg?.[`identity${k}`]?.configured ? "•••• (unchanged)" : "Bearer / token value"} value={id.headerValue} onChange={e => setId({ ...id, headerValue: e.target.value })} />
            </div>
          </div>
        ))}
      </div>

      {/* safety controls */}
      <div style={{ marginTop: 14, padding: 10, background: "#f8fafc", borderRadius: 8 }}>
        <label style={{ fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={readOnly} onChange={e => setReadOnly(e.target.checked)} />
          Read-only (GET/HEAD only — no state-changing requests)
        </label>
        {!readOnly && (
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#b45309", fontWeight: 600 }}>Allowed methods:</span>
            {METHODS.map(m => (
              <label key={m} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 3 }}>
                <input type="checkbox" checked={allowed.includes(m)} onChange={() => toggleMethod(m)} disabled={m === "GET" || m === "HEAD"} /> {m}
              </label>
            ))}
            <span style={{ fontSize: 11, color: "#94a3b8" }}>PUT/DELETE only run if you enable them.</span>
          </div>
        )}
      </div>

      {/* object test cases (BOLA/IDOR) */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Object endpoints (BOLA/IDOR) — A's ids vs B's ids</div>
        {objects.map((o, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <input style={{ ...input, width: 110 }} placeholder="name" value={o.name} onChange={e => setObj(i, "name", e.target.value)} />
            <select style={input} value={o.method} onChange={e => setObj(i, "method", e.target.value)}>{METHODS.map(m => <option key={m}>{m}</option>)}</select>
            <input style={{ ...input, flex: 1, minWidth: 180 }} placeholder="/api/shipments/{id}" value={o.path_template} onChange={e => setObj(i, "path_template", e.target.value)} />
            <input style={{ ...input, width: 120 }} placeholder="A ids (csv)" value={(o.a_ids || []).join(",")} onChange={e => setObjIds(i, "a_ids", e.target.value)} />
            <input style={{ ...input, width: 120 }} placeholder="B ids (csv)" value={(o.b_ids || []).join(",")} onChange={e => setObjIds(i, "b_ids", e.target.value)} />
            <button onClick={() => delObj(i)} style={{ background: "none", border: "none", color: "#cbd5e1", cursor: "pointer" }}><i className="fas fa-times" /></button>
          </div>
        ))}
        <button onClick={addObj} style={{ ...btn, background: "#e2e8f0", color: "#334155", padding: "5px 12px", fontSize: 13 }}>+ object endpoint</button>
      </div>

      {/* privileged endpoints (BFLA) */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>Privileged endpoints (BFLA) — should be admin-only</div>
        {privileged.map((o, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
            <input style={{ ...input, width: 110 }} placeholder="name" value={o.name} onChange={e => setPriv(i, "name", e.target.value)} />
            <select style={input} value={o.method} onChange={e => setPriv(i, "method", e.target.value)}>{METHODS.map(m => <option key={m}>{m}</option>)}</select>
            <input style={{ ...input, flex: 1 }} placeholder="/api/admin/users" value={o.path} onChange={e => setPriv(i, "path", e.target.value)} />
            <button onClick={() => delPriv(i)} style={{ background: "none", border: "none", color: "#cbd5e1", cursor: "pointer" }}><i className="fas fa-times" /></button>
          </div>
        ))}
        <button onClick={addPriv} style={{ ...btn, background: "#e2e8f0", color: "#334155", padding: "5px 12px", fontSize: 13 }}>+ privileged endpoint</button>
      </div>

      {/* actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
        <button style={{ ...btn, background: "#334155" }} onClick={save} disabled={busy}>Save engagement</button>
        <button style={{ ...btn, opacity: runnableCases === 0 ? 0.6 : 1 }} onClick={run} disabled={busy}>
          <i className="fas fa-play" style={{ marginRight: 6 }} />{busy ? "Running…" : "Run pen-test"}
        </button>
        <span style={{ fontSize: 12, color: (specUrl.trim() || runnableCases > 0) ? "#16a34a" : "#b45309" }}>
          {specUrl.trim() ? "spec-driven (auto-discover)" : `${runnableCases} case(s) ready`}
        </span>
        {cfg?.lastRunAt && <span style={{ fontSize: 12, color: "#64748b" }}>
          Last run: {new Date(cfg.lastRunAt).toLocaleString()} · {cfg.lastStatus} · {cfg.lastConfirmed ?? 0} confirmed
        </span>}
        {msg && <span style={{ fontSize: 13, color: msg.startsWith("Saved") ? "#16a34a" : "#b91c1c" }}>{msg}</span>}
      </div>

      {/* results */}
      {result && !result.error && (
        <div style={{ marginTop: 14, padding: 12, background: "#f8fafc", borderRadius: 8 }}>
          <div style={{ fontWeight: 700 }}>
            {result.confirmedCount || 0} confirmed · {result.candidatesCount || 0} candidate(s) · {result.casesRun || 0} case(s)
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}> · methods {result.allowedMethods}</span>
          </div>
          {result.discovery && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              Auto-discovered: {result.discovery.pathsInSpec} paths in spec · {result.discovery.objectEndpointsTested} object + {result.discovery.privilegedEndpointsTested} privileged endpoint(s) tested
            </div>
          )}
          {(result.findings || []).map((f, i) => (
            <div key={i} style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #e2e8f0" }}>
              <span style={{ fontWeight: 600, color: "#b91c1c" }}>{f.severity}</span> — {f.title}
              {f.cvss?.baseScore != null && <span style={{ color: "#64748b" }}> (CVSS {f.cvss.baseScore})</span>}
              <div style={{ fontSize: 12, color: "#475569" }}>{f.description}</div>
            </div>
          ))}
          {(!result.findings || result.findings.length === 0) &&
            <div style={{ color: "#16a34a", fontSize: 13, marginTop: 6 }}>No confirmed authorization findings.</div>}
        </div>
      )}
    </div>
  );
}
